import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';

/**
 * Sign out the current user — revokes the session server-side + the
 * Set-Cookie response clears the httpOnly cookie. On success, the local
 * /auth/me query is invalidated so any subsequent useCurrentUser()
 * picks up the now-signed-out state.
 *
 * Phase 14a audit follow-up (H2): on transport failure (backend
 * unreachable, network drop), we ALSO clear the client-side query cache.
 * Rationale: the user clicked "Sign out" — leaving them stuck in a
 * signed-in UI when they explicitly asked to leave is a worse failure
 * than a slightly stale client view. The server-side session may still
 * exist (it'll TTL out normally), but the client at minimum stops
 * trusting the previous identity. Callers can register their own
 * `onError` callback when calling `mutate()` if they want to surface a
 * toast.
 *
 * Caller is responsible for navigating away from authed pages after the
 * mutation succeeds or errors (the success and error callbacks both
 * mean "no longer signed in client-side").
 */
export function useSignOut() {
  const queryClient = useQueryClient();
  // Phase 17 audit fix (2026-07-15) — clear the ENTIRE query cache on
  // sign-out, not just the ['auth'] + ['me'] prefixes. Persona panels
  // cache PII under other top-level keys (['caregiver'], ['facility'],
  // ['homecare'], ['invoices'] — banking last-4, SIN status, earnings,
  // invoices, recipients). Selective removal left those entries live, so
  // on a shared browser the next user to sign in (without a hard reload)
  // could be handed the previous user's cached data until a refetch tick.
  // clear() drops everything — there is no cached query worth preserving
  // across an identity change.
  const clearLocalAuth = () => {
    queryClient.clear();
  };
  return useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/auth/sign-out');
    },
    onSuccess: clearLocalAuth,
    onError: clearLocalAuth,
  });
}
